import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { Path } from '@ariksa/inventory-core/api';
import { entries } from 'lodash';

import {
  pathToEdges,
  uniqEdges,
} from 'components/Visualization/Graphs/utils/path';
import { useGraphScaling } from 'components/Visualization/PixiGraph/hooks/useGraphScaling';
import { AccessMap } from 'components/Visualization/PixiGraph/maps/AccessMap/elements/AccessMap';
import { AccessMapLayout } from 'components/Visualization/PixiGraph/maps/AccessMap/elements/Layout';
import {
  AccessMapHeaderNode,
  AccessMapPaginationNode,
  AccessMapResourceNode,
} from 'components/Visualization/PixiGraph/maps/AccessMap/elements/Node';
import {
  NodePagination,
  useAccessMapContext,
} from 'components/Visualization/PixiGraph/maps/AccessMap/hooks/useAccessMapContext';

import { usePixiApp } from '../../../hooks/usePixiApp';
import { useRenderer } from '../../../hooks/useRenderer';
import { AccessMapEdge } from '../elements/Edge';

interface UsePostureMapProps {
  ref: RefObject<any>;
  findings?: boolean;
  resourceSearch?: string;
  showResourceSearch?: boolean;
  resourceNodes?: any[];
  resourcePaths?: Path[];
  resourcePagination?: NodePagination | null;
  resourceHeaderCount?: number;
  loadingNodeId?: string;
}

const intoHeaderNodeData = (n: any, resourceHeaderCount?: number) => {
  return {
    id: n.name,
    header: n.name,
    col: n.horizontal_order ?? 1,
    row: n.vertical_order ?? 1,
    ...n,
    ...(n.name === 'Resource' && resourceHeaderCount !== undefined
      ? { count: resourceHeaderCount }
      : {}),
  };
};

export const useAccessMap = (props: UsePostureMapProps) => {
  const {
    ref,
    findings,
    resourceSearch = '',
    showResourceSearch = false,
    resourceNodes,
    resourcePaths,
    resourcePagination,
    resourceHeaderCount,
    loadingNodeId,
  } = props;
  const {
    nodes = [],
    paths,
    ordering = [],
    pagination = {},
  } = useAccessMapContext();

  const filterNodes = useCallback(
    (nodes: any[]) => {
      const nodesWithLoadedResources = resourceNodes
        ? [
            ...nodes.filter(node => node.header !== 'Resource'),
            ...resourceNodes,
          ]
        : nodes;
      const searchTerm = resourceSearch.trim().toLowerCase();
      if (!searchTerm) return nodesWithLoadedResources;

      return nodesWithLoadedResources.filter(node => {
        if (node.header !== 'Resource') return true;

        return [
          node.name,
          node.id,
          node.identity,
          node.resource_id,
          node.info,
          node.native_name,
          node.agnostic_name,
          node.ariksaType?.resource_alias,
          node.ariksaType?.cloud_agnostic_name,
          node.ariksaType?.cloud_native_name,
        ].some(value =>
          String(value ?? '')
            .toLowerCase()
            .includes(searchTerm),
        );
      });
    },
    [resourceNodes, resourceSearch],
  );

  const graphPaths = useMemo(
    () => (resourcePaths ? [...paths, ...resourcePaths] : paths),
    [paths, resourcePaths],
  );

  const intoResourceNode = useCallback(
    (node: any) =>
      new AccessMapResourceNode({
        ...node,
        isLoading: (node.id ?? node.identity) === loadingNodeId,
      }),
    [loadingNodeId],
  );

  const paginationNodes = useCallback(
    (
      pagination: Record<string, NodePagination>,
      resourcePagination?: NodePagination | null,
    ) => {
      const pages = entries(pagination)
        .filter(
          ([header]) =>
            resourcePagination === undefined || header !== 'Resource',
        )
        .map(([header, page]) => {
          return new AccessMapPaginationNode({
            id: header,
            header: header,
            visibleCount: page.visibleCount,
            totalCount: page.totalCount,
          });
        }, [] as AccessMapPaginationNode[]);

      if (resourcePagination) {
        pages.push(
          new AccessMapPaginationNode({
            id: resourcePagination.header,
            header: resourcePagination.header,
            visibleCount: resourcePagination.visibleCount,
            totalCount: resourcePagination.totalCount,
            remote: true,
            cursor: resourcePagination.cursor,
          }),
        );
      }

      return pages;
    },
    [],
  );

  const [graph] = useState(() => {
    const filteredNodes = filterNodes(nodes);
    const targetNodes = filteredNodes.filter(n => n.is_target);
    const innerNodes = filteredNodes.filter(n => !n.is_target);

    const layout = new AccessMapLayout();
    return new AccessMap({
      headers: ordering.map(
        n =>
          new AccessMapHeaderNode(intoHeaderNodeData(n, resourceHeaderCount)),
      ),
      pagination: paginationNodes(pagination, resourcePagination),
      nodes: [...innerNodes, ...targetNodes].map(intoResourceNode),
      edges: uniqEdges(pathToEdges(graphPaths).map(e => new AccessMapEdge(e))),
      paths: graphPaths,
      layout,
      styles: {
        padding: [20, 10],
        scale: 1,
        showResourceSearch,
      },
      findings,
    });
  });

  const app = usePixiApp({ ref });
  useGraphScaling({ app, graph, ref });
  const renderer = useRenderer({ app, graph });

  useEffect(() => {
    const filteredNodes = filterNodes(nodes);
    const targetNodes = filteredNodes.filter(n => n.is_target);
    const innerNodes = filteredNodes.filter(n => !n.is_target);

    graph.updateData({
      headers: ordering.map(
        n =>
          new AccessMapHeaderNode(intoHeaderNodeData(n, resourceHeaderCount)),
      ),
      pagination: paginationNodes(pagination, resourcePagination),
      nodes: [...innerNodes, ...targetNodes].map(intoResourceNode),
      edges: uniqEdges(pathToEdges(graphPaths).map(e => new AccessMapEdge(e))),
      paths: graphPaths,
      findings,
    });

    renderer.update();
  }, [
    app,
    nodes,
    graph,
    renderer,
    ordering,
    graphPaths,
    paginationNodes,
    pagination,
    resourcePagination,
    resourceHeaderCount,
    findings,
    filterNodes,
    intoResourceNode,
  ]);

  // Keep headings at the top while centering the graph horizontally.
  useEffect(() => {
    if (!ref.current) return;

    const visibleNodes = [
      ...graph.headers,
      ...graph.nodes.filter(node => node.visible),
      ...graph.pagination.filter(node => node.visible),
    ];
    if (!visibleNodes.length) return;

    const minX = Math.min(...visibleNodes.map(node => node.x));
    const minY = Math.min(...visibleNodes.map(node => node.y));
    const maxX = Math.max(...visibleNodes.map(node => node.x + node.w));
    const maxY = Math.max(...visibleNodes.map(node => node.y + node.h));
    const scale = app.stage.scale.x;
    const {
      offsetHeight: viewportHeight,
      offsetWidth: viewportWidth,
    } = ref.current;

    app.stage.x = (viewportWidth - (maxX - minX) * scale) / 2 - minX * scale;
    app.stage.y = (graph.styles.padding[1] ?? 0) + 20 - minY * scale;
    renderer.update();
  }, [app, renderer, graph, ref]);

  // @ts-ignore
  window.graph = graph;

  return {
    app,
    graph,
    renderer,
  };
};
